import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle } from 'lucide-react';
import { contact } from '../utils/api';
import Logo from '../components/Logo';

/**
 * Public Data Subject Request (DSR) page.
 *
 * For people who are the SUBJECT of a due-diligence report (or believe they
 * may be) and want to exercise their rights under UK GDPR — access (Art 15),
 * rectification (Art 16), erasure (Art 17), or objection to processing (Art 21).
 *
 * Customers should use the in-account deletion / data-export flow instead.
 * This page is for non-customers — they don't have credentials, so it lives
 * outside auth.
 *
 * The submission goes to the DPO mailbox via backend/services/email.
 * SLA: 30 days under UK GDPR.
 */
const DataRequest = () => {
  const [type, setType] = useState('erasure');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { reference }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await contact.dataRequest({ type, name, email, subjectName, message });
      setSuccess(result);
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please email dpo-id100@totalinfo.net directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
            <Logo />
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white border rounded-lg p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Request received</h1>
            <p className="text-gray-700 mb-4">Thank you. Your data subject request has been logged with our Data Protection Officer.</p>
            <div className="bg-gray-100 rounded p-4 my-6">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Reference</p>
              <p className="font-mono text-lg text-gray-900">{success.reference}</p>
            </div>
            <p className="text-sm text-gray-600">We will respond within <strong>30 days</strong> as required by UK GDPR. If you need to follow up, quote the reference above and email <a href="mailto:dpo-id100@totalinfo.net" className="text-red-600 underline">dpo-id100@totalinfo.net</a>.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <Logo />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-8 w-8 text-red-600" />
          <h1 className="text-3xl font-bold text-gray-900">Data Subject Request</h1>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <p className="text-sm text-blue-900">
            <strong>Are you a customer?</strong> If you have an id100 account, please log in and use the
            "Export my data" or "Delete my account" buttons in your account settings — those flows are faster.
            This form is primarily for people who are the <em>subject</em> of a report and want to exercise
            their rights under UK GDPR.
          </p>
        </div>

        <p className="text-gray-700 mb-6">
          Under UK GDPR, you have rights including access to your personal data, correction of inaccurate
          data, erasure ("right to be forgotten"), and objection to processing. Submit your request below
          and our Data Protection Officer will respond within 30 days.
        </p>

        <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type of request *</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              required
            >
              <option value="access">Access — what data do you hold about me?</option>
              <option value="rectification">Rectification — correct inaccurate data</option>
              <option value="erasure">Erasure — delete my data ("right to be forgotten")</option>
              <option value="objection">Objection — object to processing</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your contact email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="you@example.com"
            />
            <p className="mt-1 text-xs text-gray-500">We will reply to this address.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject of the report (if known)</label>
            <input
              type="text"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="e.g. company name or individual name"
            />
            <p className="mt-1 text-xs text-gray-500">If you're requesting data about yourself, this is the name you'd expect to appear in any report.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Details of your request *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="Describe your request, including any reference numbers, dates, or specific data you're asking about."
            />
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-medium px-6 py-2 rounded-md"
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>

        <p className="text-xs text-gray-500 mt-6">
          We process this request on the basis of our legal obligation to respond to data subject rights
          requests under UK GDPR. The information you provide here is used only to verify and respond to
          your request, and is retained for as long as required to demonstrate compliance.
        </p>
      </main>
    </div>
  );
};

export default DataRequest;
